import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCollaborationStore } from "@/stores/collaboration/collaboration-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useCollaborationEditorBridge } from "@/stores/editor/collaboration-editor-bridge";
import { socketClient } from "@/services/websocket/socketClient";
import { Position } from "@/types";

interface CursorData {
  userId: string;
  username: string;
  color: string;
  position: Position;
  lastUpdate: number;
  isActive: boolean;
}

interface SelectionData {
  userId: string;
  username: string;
  color: string;
  componentIds: string[];
  lastUpdate: number;
}

interface CursorUpdateData {
  userId: string;
  payload: {
    position: Position;
  };
}

interface SelectionUpdateData {
  userId: string;
  payload: {
    selection: string[];
  };
}

interface RealtimeCursorsProps {
  canvasRef: React.RefObject<HTMLElement>;
  zoom?: number;
  offset?: { x: number; y: number };
}

interface CursorTimeouts {
  inactive: ReturnType<typeof setTimeout>;
  remove: ReturnType<typeof setTimeout>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const parsePosition = (value: unknown): Position | null => {
  if (
    !isRecord(value) ||
    !isFiniteNumber(value.x) ||
    !isFiniteNumber(value.y)
  ) {
    return null;
  }

  return {
    x: value.x,
    y: value.y,
    z: isFiniteNumber(value.z) ? value.z : 0,
  };
};

const parseCursorUpdateData = (value: unknown): CursorUpdateData | null => {
  if (!isRecord(value) || typeof value.userId !== "string") {
    return null;
  }

  const { payload } = value;
  if (!isRecord(payload)) {
    return null;
  }

  const position = parsePosition(payload.position);
  if (!position) {
    return null;
  }

  return {
    userId: value.userId,
    payload: { position },
  };
};

const parseSelectionUpdateData = (
  value: unknown,
): SelectionUpdateData | null => {
  if (!isRecord(value) || typeof value.userId !== "string") {
    return null;
  }

  const { payload } = value;
  if (!isRecord(payload) || !Array.isArray(payload.selection)) {
    return null;
  }

  const selection = payload.selection;
  if (!selection.every((componentId) => typeof componentId === "string")) {
    return null;
  }

  return {
    userId: value.userId,
    payload: { selection },
  };
};

export const RealtimeCursors: React.FC<RealtimeCursorsProps> = ({
  canvasRef,
  zoom = 1,
  offset = { x: 0, y: 0 },
}) => {
  const { user: currentUser } = useAuthStore();
  const {
    activeUsers,
    isActive: isCollaborationActive,
    dragConflicts,
  } = useCollaborationStore();

  const [cursors, setCursors] = useState<Map<string, CursorData>>(new Map());
  const [selections, setSelections] = useState<Map<string, SelectionData>>(
    new Map(),
  );
  const activeUsersRef = useRef(activeUsers);
  const cursorTimeoutsRef = useRef<Map<string, CursorTimeouts>>(new Map());

  const clearCursorTimeouts = useCallback((userId: string) => {
    const timeouts = cursorTimeoutsRef.current.get(userId);
    if (!timeouts) return;

    clearTimeout(timeouts.inactive);
    clearTimeout(timeouts.remove);
    cursorTimeoutsRef.current.delete(userId);
  }, []);

  useEffect(() => {
    activeUsersRef.current = activeUsers;
  }, [activeUsers]);

  // Track current user's mouse position
  useEffect(() => {
    if (!isCollaborationActive || !canvasRef.current) return;

    const canvas = canvasRef.current;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const position = {
        x: (e.clientX - rect.left - offset.x) / zoom,
        y: (e.clientY - rect.top - offset.y) / zoom,
        z: 0,
      };

      // Send cursor update via collaboration bridge
      try {
        // Using useCollaborationEditorBridge from import
        const bridge = useCollaborationEditorBridge.getState();
        if (bridge.isCollaborationActive) {
          bridge.sendCursorUpdate(position);
        }
      } catch {
        // Bridge not available
      }
    };

    const handleMouseLeave = () => {
      // Mouse left canvas
    };

    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [isCollaborationActive, canvasRef, zoom, offset]);

  // Listen for remote cursor updates
  useEffect(() => {
    if (!isCollaborationActive) return;

    const cursorTimeouts = cursorTimeoutsRef.current;

    const handleCursorUpdate = (data: unknown) => {
      const cursorUpdate = parseCursorUpdateData(data);
      if (!cursorUpdate) return;
      if (cursorUpdate.userId === currentUser?.id) return; // Ignore own cursor

      const userSession = activeUsersRef.current.get(cursorUpdate.userId);
      if (!userSession) return;

      const cursorData: CursorData = {
        userId: cursorUpdate.userId,
        username: userSession.user.username || userSession.user.email,
        color: userSession.color,
        position: cursorUpdate.payload.position,
        lastUpdate: Date.now(),
        isActive: true,
      };

      setCursors((prev) => new Map(prev).set(cursorUpdate.userId, cursorData));
      clearCursorTimeouts(cursorUpdate.userId);

      // Auto-hide inactive cursors after 5 seconds
      const inactiveTimeout = setTimeout(() => {
        setCursors((prev) => {
          const updated = new Map(prev);
          const cursor = updated.get(cursorUpdate.userId);
          if (cursor && cursor.lastUpdate === cursorData.lastUpdate) {
            updated.set(cursorUpdate.userId, { ...cursor, isActive: false });
          }
          return updated;
        });
      }, 5000);

      // Remove completely inactive cursors after 30 seconds
      const removeTimeout = setTimeout(() => {
        setCursors((prev) => {
          const updated = new Map(prev);
          const cursor = updated.get(cursorUpdate.userId);
          if (cursor && cursor.lastUpdate === cursorData.lastUpdate) {
            updated.delete(cursorUpdate.userId);
          }
          return updated;
        });
        cursorTimeouts.delete(cursorUpdate.userId);
      }, 30000);

      cursorTimeouts.set(cursorUpdate.userId, {
        inactive: inactiveTimeout,
        remove: removeTimeout,
      });
    };

    const handleSelectionUpdate = (data: unknown) => {
      const selectionUpdate = parseSelectionUpdateData(data);
      if (!selectionUpdate) return;
      if (selectionUpdate.userId === currentUser?.id) return; // Ignore own selection

      const userSession = activeUsersRef.current.get(selectionUpdate.userId);
      if (!userSession) return;

      const selectionData: SelectionData = {
        userId: selectionUpdate.userId,
        username: userSession.user.username || userSession.user.email,
        color: userSession.color,
        componentIds: selectionUpdate.payload.selection,
        lastUpdate: Date.now(),
      };

      setSelections((prev) =>
        new Map(prev).set(selectionUpdate.userId, selectionData),
      );
    };

    // Register event handlers
    const unsubscribers = [
      socketClient.on("cursorUpdate", handleCursorUpdate),
      socketClient.on("selectionUpdate", handleSelectionUpdate),
    ];

    return () => {
      // Cleanup event handlers
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      cursorTimeouts.forEach((timeouts) => {
        clearTimeout(timeouts.inactive);
        clearTimeout(timeouts.remove);
      });
      cursorTimeouts.clear();
    };
  }, [isCollaborationActive, currentUser?.id, clearCursorTimeouts]);

  // Clean up cursors for users who left
  useEffect(() => {
    setCursors((prev) => {
      const updated = new Map(prev);
      for (const userId of updated.keys()) {
        if (!activeUsers.has(userId)) {
          updated.delete(userId);
          clearCursorTimeouts(userId);
        }
      }
      return updated;
    });

    setSelections((prev) => {
      const updated = new Map(prev);
      for (const userId of updated.keys()) {
        if (!activeUsers.has(userId)) {
          updated.delete(userId);
        }
      }
      return updated;
    });
  }, [activeUsers, clearCursorTimeouts]);

  if (!isCollaborationActive || !canvasRef.current) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute inset-0 z-50">
      {/* Remote user cursors */}
      <AnimatePresence>
        {Array.from(cursors.values()).map((cursor) => {
          const screenX = cursor.position.x * zoom + offset.x;
          const screenY = cursor.position.y * zoom + offset.y;

          return (
            <motion.div
              key={cursor.userId}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{
                opacity: cursor.isActive ? 1 : 0.3,
                scale: 1,
                x: screenX,
                y: screenY,
              }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="absolute pointer-events-none"
              style={{ color: cursor.color }}
            >
              {/* Cursor pointer */}
              <div className="relative">
                {/* Check if user is in conflict */}
                {(() => {
                  const isInConflict = Array.from(dragConflicts.values()).some(
                    (conflict) =>
                      conflict.users.some((u) => u.userId === cursor.userId) &&
                      conflict.status === "active",
                  );

                  return (
                    <div
                      className={`relative ${isInConflict ? "animate-pulse" : ""}`}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="drop-shadow-lg"
                      >
                        <path
                          d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19841L11.7841 12.3673H5.65376Z"
                          fill={cursor.color}
                          stroke={isInConflict ? "#ef4444" : "white"}
                          strokeWidth={isInConflict ? "2" : "1"}
                        />
                      </svg>

                      {/* Conflict indicator */}
                      {isInConflict && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-white animate-ping" />
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* User label */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute top-6 left-2 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-white shadow-lg"
                style={{ backgroundColor: cursor.color }}
              >
                {cursor.username}
              </motion.div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Remote user selections */}
      <AnimatePresence>
        {Array.from(selections.values()).map((selection) =>
          selection.componentIds.map((componentId) => {
            // Get component element to highlight
            const componentElement = document.querySelector(
              `[data-component-id="${componentId}"]`,
            );
            if (!componentElement) return null;

            const rect = componentElement.getBoundingClientRect();
            const canvasRect = canvasRef.current?.getBoundingClientRect();
            if (!canvasRect) return null;

            const relativeRect = {
              left: rect.left - canvasRect.left,
              top: rect.top - canvasRect.top,
              width: rect.width,
              height: rect.height,
            };

            return (
              <motion.div
                key={`${selection.userId}-${componentId}`}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="absolute pointer-events-none"
                style={{
                  left: relativeRect.left,
                  top: relativeRect.top,
                  width: relativeRect.width,
                  height: relativeRect.height,
                  border: `2px solid ${selection.color}`,
                  borderRadius: "4px",
                  backgroundColor: `${selection.color}10`,
                }}
              >
                {/* Selection label */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-7 left-0 rounded px-2 py-1 text-xs font-medium text-white shadow-sm"
                  style={{ backgroundColor: selection.color }}
                >
                  {selection.username}
                </motion.div>
              </motion.div>
            );
          }),
        )}
      </AnimatePresence>
    </div>
  );
};

export default RealtimeCursors;
